import { useMemo } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { useCatalog, formatUSD } from "@/lib/CatalogProvider";
import { SectionHead } from "@/components/inquiry/inquiryFields";

/**
 * StepPackage — select a package from the CMS catalogue.
 *
 * Displays branding + video packages grouped by category. Also exposes
 * a 'Custom Engagement' card for users who want a fully bespoke scope
 * without leaving the Package Journey.
 */
export const StepPackage = ({ data, onSelect, onSelectCustom }) => {
    const { packages } = useCatalog();

    const grouped = useMemo(() => {
        const visible = (packages || []).filter((p) => p.visible !== false && p.kind !== "video").sort((a, b) => (a.order || 0) - (b.order || 0));
        return {
            logo: visible.filter((p) => p.kind === "logo"),
            branding: visible.filter((p) => p.kind === "branding"),
        };
    }, [packages]);

    const isSelected = (p) => !data.is_custom_scope && data.selected_package === p.name;

    const Card = ({ p, idx }) => {
        const selected = isSelected(p);
        const isVideo = p.kind === "video";
        return (
            <motion.button
                key={p.id}
                type="button"
                onClick={() => onSelect(p)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.03 * idx, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -2 }}
                data-testid={`package-card-${p.id}`}
                data-package-id={p.id}
                className="relative flex h-full flex-col items-start rounded-2xl border p-5 text-left transition-all"
                style={{
                    borderColor: selected ? (isVideo ? "#6FE3DD" : "var(--pg-accent)") : "var(--pg-border)",
                    background: selected
                        ? (isVideo
                            ? "linear-gradient(135deg, rgba(47,227,208,0.08) 0%, var(--pg-surface) 100%)"
                            : "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 8%, transparent) 0%, var(--pg-surface) 100%)")
                        : "var(--pg-surface)",
                    boxShadow: selected
                        ? (isVideo ? "0 12px 40px rgba(47,227,208,0.18)" : "0 12px 40px color-mix(in srgb, var(--pg-accent) 15%, transparent)")
                        : "none",
                }}
            >
                <div className="flex w-full items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                            {p.category}
                        </div>
                        <h4 className="font-display mt-1.5 text-lg leading-tight" style={{ color: "var(--pg-fg)" }}>
                            {p.name}
                        </h4>
                    </div>
                    {selected && (
                        <span
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                            style={{ background: isVideo ? "#6FE3DD" : "var(--pg-accent)", color: "#071112" }}
                        >
                            <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
                        </span>
                    )}
                </div>

                {p.description && (
                    <p className="mt-3 text-xs leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                        {p.description}
                    </p>
                )}

                {Array.isArray(p.features) && p.features.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`package-features-${p.id}`}>
                        {p.features.map((f, fi) => {
                            const count = p.features.length;
                            const step = count > 1 ? Math.min(2.8 / (count - 1), 0.4) : 0;
                            return (
                                <motion.span
                                    key={fi}
                                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.4, delay: fi * step, ease: [0.16, 1, 0.3, 1] }}
                                    className="pg-pkg-feature"
                                >
                                    <Check className="h-3 w-3" strokeWidth={2.4} /> {f}
                                </motion.span>
                            );
                        })}
                    </div>
                )}

                <div className="mt-auto pt-5 flex w-full items-baseline justify-between">
                    <span
                        className={`pg-price-num text-lg ${isVideo ? "pg-price-cyan" : "pg-price-gold"}`}
                    >
                        {p.price_label || p.priceLabel || formatUSD(p.base_price)}
                    </span>
                    {p.ribbon && (
                        <span
                            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-medium tracking-normal"
                            style={{
                                background: "color-mix(in srgb, var(--pg-accent) 18%, transparent)",
                                color: "var(--pg-accent)",
                            }}
                        >
                            <Sparkles className="h-2.5 w-2.5" /> {p.ribbon}
                        </span>
                    )}
                </div>
            </motion.button>
        );
    };

    const Group = ({ title, items, testId }) => {
        if (!items || items.length === 0) return null;
        return (
            <div data-testid={testId}>
                <div className="mb-4 flex items-baseline justify-between">
                    <h3
                        className="font-display text-lg"
                        style={{ color: "var(--pg-fg)" }}
                    >
                        {title}
                    </h3>
                    <span className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                        {items.length} option{items.length === 1 ? "" : "s"}
                    </span>
                </div>
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                    {items.map((p, idx) => <Card key={p.id} p={p} idx={idx} />)}
                </div>
            </div>
        );
    };

    return (
        <section data-testid="step-package">
            <SectionHead
                eyebrow="Step 02 · Package"
                title="Pick the package that fits."
                description="Every option includes strategy, execution, and premium delivery. Not sure? Choose the closest fit — we'll fine-tune scope with you."
            />

            <div className="space-y-10">
                <Group title="Logo Systems" items={grouped.logo} testId="package-group-logo" />
                <Group title="Brand Identity" items={grouped.branding} testId="package-group-branding" />
            </div>

            {/* Custom Engagement fallback — keeps single-flow UX for bespoke scope */}
            <motion.button
                type="button"
                onClick={onSelectCustom}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -2 }}
                data-testid="package-card-custom-engagement"
                className="mt-10 flex w-full flex-col items-start rounded-2xl border-2 border-dashed p-6 text-left transition-all"
                style={{
                    borderColor: data.is_custom_scope ? "var(--pg-accent)" : "var(--pg-border-strong)",
                    background: data.is_custom_scope
                        ? "linear-gradient(135deg, color-mix(in srgb, var(--pg-accent) 8%, transparent) 0%, var(--pg-surface) 100%)"
                        : "var(--pg-surface)",
                }}
            >
                <div className="flex w-full items-start justify-between gap-3">
                    <div>
                        <div className="text-[11px] font-medium tracking-normal" style={{ color: "var(--pg-fg-muted)" }}>
                            Custom · Bespoke
                        </div>
                        <h4 className="font-display mt-1.5 text-xl" style={{ color: "var(--pg-fg)" }}>
                            Custom Project
                        </h4>
                        <p className="mt-2 max-w-md text-xs leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                            Skip fixed pricing and go straight into the guided Custom Project Inquiry.
                            You'll pick your own services, share your vision, and we'll reply with a
                            tailored Custom Engagement proposal.
                        </p>
                    </div>
                    {data.is_custom_scope && (
                        <span
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                            style={{ background: "var(--pg-accent)", color: "#071112" }}
                        >
                            <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
                        </span>
                    )}
                </div>
            </motion.button>
        </section>
    );
};

export default StepPackage;
